import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import IrpAppService from '../../../../application/platform/scp/irp/irp-app-service';
import BaseAppService from '../../../../application/platform/scp/base/base-app-service';
import EntityAppService from '../../../../application/platform/scp/entity/entitiy-app-service';
import { ACTION_TYPES, useIrpCreateEditDispatch, useIrpCreateEditState } from './irp-create-edit-context';
import { DefaultDialogProps } from '../../../../components/default-dialog';
import { useBaseLayout } from '../../../../components/context/use-base-layout';
import { IIrpAddEditRequest } from '../../../../model/scp/irp/request/irp-add-edit-request';
import { useBaseData } from '../../../../application/context/base-data-context';
import { sanitizeText } from '../../../../utils/text';

export const useIrpCreateEditController = () => {
    const navigate = useNavigate();
    const { showLoading, showMessage, showDialog } = useBaseLayout();
    const [isSaving, setIsSaving] = useState(false);
    const dispatch = useIrpCreateEditDispatch();
    const state = useIrpCreateEditState();
    const baseData = useBaseData();

    useEffect(() => {
        loadInitialData();
    }, []);

    useEffect(() => {
        if (baseData.loaded) {
            dispatch({ type: ACTION_TYPES.setInstrumentSummons, payload: baseData.instrumentSummons });
            dispatch({ type: ACTION_TYPES.setModality, payload: baseData.modality });
            dispatch({ type: ACTION_TYPES.setDisputeMode, payload: baseData.disputeMode });
        }
    }, [baseData.loaded]);

    async function loadInitialData() {
        try {
            showLoading({ open: true });

            const [legalSupport, typeDocument, unidEntity] = await Promise.all([
                BaseAppService.listLegalSupport(),
                BaseAppService.listTypeDocument(6),
                EntityAppService.listUnidEntity()
            ]);

            dispatch({ type: ACTION_TYPES.setLegalSupport, payload: legalSupport });
            dispatch({ type: ACTION_TYPES.setTypeDocument, payload: typeDocument });
            dispatch({ type: ACTION_TYPES.setUnidEntity, payload: unidEntity });

            if (state.irpId !== undefined && state.irpId !== null) {
                const data = await IrpAppService.getIrp(state.irpId);

                // Recarregar Amparo Legal ANTES de setar o irpRequest
                // para garantir que a lista contenha o valor correto
                if (data.typeCallingInstrumentId || data.modalityId) {
                    const filteredLegalSupport = await BaseAppService.listLegalSupport(
                        data.typeCallingInstrumentId,
                        data.modalityId
                    );
                    dispatch({ type: ACTION_TYPES.setLegalSupport, payload: filteredLegalSupport });
                }

                // Mapear os dados da API para o formato do formulário
                const mappedData: IIrpAddEditRequest = {
                    id: data.id,
                    clientId: data.clientId,
                    codManagingUnit: data.codManagingUnit || '',
                    typeCallingInstrumentId: data.typeCallingInstrumentId,
                    modalityId: data.modalityId,
                    disputeModeId: data.disputeModeId,
                    legalSupportId: data.legalSupportId,
                    numberIrp: data.numberIrp || '',
                    yearIrp: data.yearIrp,
                    processNumber: data.processNumber,
                    objectIrp: data.objectIrp || '',
                    complementaryInformation: data.complementaryInformation || '',
                    indPossibilityAdhesion: data.indPossibilityAdhesion,
                    finalInterestManifestationDate: data.finalInterestManifestationDate,
                    urlSystemOrigin: data.urlSystemOrigin,
                    urlElectronicProcess: data.urlElectronicProcess,
                };

                dispatch({ type: ACTION_TYPES.setIrpRequest, payload: mappedData });
            }

            showLoading({ open: false });
        } catch (error) {
            showMessage({
                open: true,
                message: 'Não foi possível carregar os dados da intenção de registro de preço',
                detail: (error as any).details || (error as Error).message,
                type: 'error'
            });
            showLoading({ open: false });
        }
    }

    function handleTabChange(event: any, newTabIndex: number) {
        dispatch({ type: ACTION_TYPES.setTabIndex1, payload: newTabIndex });
    }

    function handleOnChange(e: any) {
        const updates: any = {};
        const fieldMap: Record<string, string> = {
            'select-legal-support': 'legalSupportId',
            'select-type-calling-instrument': 'typeCallingInstrumentId',
            'select-modality': 'modalityId',
            'select-type-document': 'documentTypeId',
            'select-dispute-mode': 'disputeModeId',
            'select-managing-unit': 'codManagingUnit',
            'input-year-irp': 'yearIrp',
            'input-number-irp': 'numberIrp',
            'input-process-number': 'processNumber',
            'input-object': 'objectIrp',
            'input-title': 'title',
            'input-complementary-information': 'complementaryInformation',
            'input-url-system-origin': 'urlSystemOrigin',
            'input-url-electronic-process': 'urlElectronicProcess',
            'input-justification': 'justification',
            'select-client': 'clientId',
        };

        const textFields = new Set(['input-object', 'input-title', 'input-complementary-information']);

        if (e.target.name === 'check-ind-possibility-adhesion') {
            updates.indPossibilityAdhesion = e.target.checked;
        } else if (fieldMap[e.target.name]) {
            updates[fieldMap[e.target.name]] = textFields.has(e.target.name)
                ? sanitizeText(e.target.value)
                : e.target.value;
        }

        dispatch({ type: ACTION_TYPES.setIrpRequest, payload: updates });

        // Recarrega Amparo Legal quando Instrumento Convocatório ou Modalidade mudar
        if (e.target.name === 'select-type-calling-instrument' || e.target.name === 'select-modality') {
            reloadLegalSupport(
                e.target.name === 'select-type-calling-instrument' ? e.target.value : state.irpRequest.typeCallingInstrumentId,
                e.target.name === 'select-modality' ? e.target.value : state.irpRequest.modalityId
            );
        }
    }

    async function reloadLegalSupport(instrumentSummonsId?: number, modalityId?: number) {
        try {
            const legalSupport = await BaseAppService.listLegalSupport(instrumentSummonsId, modalityId);
            dispatch({ type: ACTION_TYPES.setLegalSupport, payload: legalSupport });

            // Limpa o campo de Amparo Legal selecionado quando os filtros mudarem
            dispatch({ type: ACTION_TYPES.setIrpRequest, payload: { legalSupportId: 0 } });
        } catch (error) {
            showMessage({
                open: true,
                message: 'Não foi possível recarregar o amparo legal',
                detail: (error as any).details || (error as Error).message,
                type: 'error'
            });
        }
    }

    function handleDateChange(date: any, field: string) {
        const updates: any = {};
        if (date && date.isValid && date.isValid()) {
            updates[field] = date.format('YYYY-MM-DD');
        } else {
            updates[field] = null;
        }
        dispatch({ type: ACTION_TYPES.setIrpRequest, payload: updates });
    }

    function setFileUpload(file: File) {
        dispatch({ type: ACTION_TYPES.setFile, payload: file });
    }

    function setItemsIrpJson(json: string) {
        dispatch({ type: ACTION_TYPES.setItemsIrpJson, payload: json });
    }

    function validFields(): boolean {
        const { irpRequest } = state;

        if (!state.irpId && (!irpRequest.title || irpRequest.title.length === 0)) return false;
        if (!state.irpId && (!irpRequest.documentTypeId || irpRequest.documentTypeId === 0)) return false;
        if (!state.irpId && !state.file) return false;
        if (!irpRequest.codManagingUnit || irpRequest.codManagingUnit.length === 0) return false;
        if (!irpRequest.typeCallingInstrumentId || irpRequest.typeCallingInstrumentId === 0) return false;
        if (!irpRequest.modalityId || irpRequest.modalityId === 0) return false;
        if (!irpRequest.disputeModeId || irpRequest.disputeModeId === 0) return false;
        if (!irpRequest.legalSupportId || irpRequest.legalSupportId === 0) return false;
        if (!irpRequest.yearIrp || irpRequest.yearIrp === 0) return false;
        if (!irpRequest.numberIrp || irpRequest.numberIrp.length === 0) return false;
        if (!irpRequest.objectIrp || irpRequest.objectIrp.length === 0) return false;
        if (!irpRequest.clientId || irpRequest.clientId === 0) return false;
        if (!irpRequest.urlSystemOrigin || irpRequest.urlSystemOrigin.length === 0) return false;
        if (!irpRequest.finalInterestManifestationDate) return false;

        return true;
    }

    async function addOrEditIrp() {
        if (!validFields()) {
            dispatch({ type: ACTION_TYPES.setShowValidationErrors, payload: true });
            showMessage({
                open: true,
                message: "Preencha todos os campos obrigatórios",
                type: "error"
            });
            return;
        }

        // Validação: IRP deve ter pelo menos um item ao criar
        if (state.irpId === undefined || state.irpId === null) {
            try {
                const items = JSON.parse(state.itemsIrpJson || '[]');
                if (!items || items.length === 0) {
                    showMessage({
                        open: true,
                        message: "É necessário adicionar pelo menos um item à intenção de registro de preço",
                        type: "error"
                    });
                    return;
                }
            } catch (error) {
                showMessage({
                    open: true,
                    message: "É necessário adicionar pelo menos um item à intenção de registro de preço",
                    type: "error"
                });
                return;
            }
        }

        setIsSaving(true);
        try {
            showLoading({ open: true });

            if (state.irpId === undefined || state.irpId === null) {
                await IrpAppService.addIrp(state.irpRequest, state.file!, state.itemsIrpJson);
                showMessage({ open: true, message: "Intenção de registro de preço adicionada com sucesso!", type: "success" });
            } else {
                await IrpAppService.updateIrp(state.irpId, state.irpRequest);
                showMessage({ open: true, message: "Intenção de registro de preço atualizada com sucesso!", type: "success" });
            }

            showLoading({ open: false });

            showDialog({
                open: true,
                message: "Deseja adicionar uma nova intenção de registro de preço?",
                onYesClick: () => { navigate("/intencao-registro-preco/publicar"); },
                onNoClick: () => { navigate("/intencao-registro-preco"); }
            } as DefaultDialogProps);
        } catch (error) {
            showMessage({
                open: true,
                message: "Não foi possível salvar a intenção de registro de preço",
                detail: (error as any).details || (error as Error).message,
                type: "error"
            });
            showLoading({ open: false });
        } finally {
            setIsSaving(false);
        }
    }

    return {
        handleTabChange,
        handleOnChange,
        handleDateChange,
        setFileUpload,
        setItemsIrpJson,
        validFields,
        addOrEditIrp,
        isSaving
    };
}
